<template>
    <div class="lp-location-finder" :class="cssClass">
        <div class="lp-location-finder__list">
            <div class="container">
                <div class="input-group input-group--short">
                    <button class="btn btn-secondary"
                            type="button"
                            aria-label="Get location"
                            @click="getLocation">
                        <i class="far fa-location"></i>
                    </button>
                    <input type="text"
                           class="form-control"
                           :placeholder="searchPlaceholder"
                           v-model="searchInput"
                           @input="searchHandler"
                           aria-label="Search locations" />
                </div>
                <p class="lp-location-finder__filters-title">Filters</p>
                <tag-filter :options="sharedSource.arr"
                            class="lp-shared-source"
                            @option-clicked="sharedSourceClickHandler" />
                <tag-filter :options="ourBranch.arr"
                            class="mb-4"
                            @option-clicked="ourBranchClickHandler" />
                <tag-filter :options="filters.arr"
                            class="mb-4"
                            @option-clicked="filterClickHadler" />
                <div class="lp-location-finder-list__locations"
                     v-if="visibleLocations.length > 0">
                    <location-list-item v-for="(location, index) in visibleLocations"
                                        :location="location"
                                        :index="index"
                                        :details-url="detailsUrl" />
                </div>
                <p class="h4" v-else>No locations match the current filter.</p>
            </div>
        </div>
        <div class="lp-location-finder__map" aria-hidden="true">
            <google-map :apiKey="apiKey"
                        :initialMarker="false"
                        :center="{ lat: latitude, lng: longitude }"
                        :markers="markers"
                        :zoom="currentZoom"
                        :zip-code="searchInput" />
        </div>
    </div>
</template>

<script>
    import { computed, reactive, ref, onMounted } from 'vue';
    import { formatPhoneNumber } from '../helpers/formatting';
    import { orderByObjectNumber } from '../helpers/sorting';
    import TagFilter from './TagFilter.vue';
    import GoogleMap from './GoogleMap.vue';
    import LocationListItem from "./LocationListItem.vue";
    import { distanceInMiBetweenEarthCoordinates } from '../helpers/locationHelpers';
    import LocationPin from "./LocationPin.vue";
    import { Loader } from '@googlemaps/js-api-loader';

    export default {
        components: { TagFilter, GoogleMap, LocationPin, LocationListItem },
        name: 'LocationFinder',
        props: {
            apiKey: {
                type: String,
                required: true,
            },
            center: {
                type: Object || null,
                required: true,
            },
            config: {
                type: Object,
                required: true
            },
            cssClass: {
                type: String,
                required: false,
                default: '',
            },
            detailsUrl: {
                type: String,
                required: true,
            },
            locations: {
                type: Array || null,
                required: true,
            },
            locationTypes: {
                type: Array || null,
                required: true,
            },
            customFilters: {
                type: Array || null,
                required: true,
            },
            zoom: {
                type: Number,
                required: false,
                default: 10,
            },
            filterType: {
                type: String,
                required: true,
            },
        },
        setup(props) {
            // Initialize Search
            const searchInput = ref('');
            const search = ref('');
            let searchTimeout;
            const sharedSource = reactive({ arr: [] });

            const loader = new Loader({
                apiKey: props.apiKey,
                version: 'weekly',
                libraries: ['places'],
            });

            const ourBranch = reactive({
                arr: props.locationTypes
                    .filter(elm => elm.ExternalSource == "1")
                    .map(item => {

                        if (item.ImageUrl && item.ImageUrl != "") {
                            urlContentToDataUri(item.ImageUrl)
                                .then(dataUrl => {
                                    ourBranch.arr = ourBranch.arr.map(elm => {
                                        if (elm.id == item.Id) {
                                            return ({
                                                ...elm,
                                                base64Icon: dataUrl
                                            })
                                        }
                                        return elm
                                    })
                                });
                        }

                        return {
                            id: item.Id,
                            name: item.Title,
                            selected: true,
                            backgroundColor: item.BackgroundColor,
                            textColor: item.TextColor,
                            icon: item.ImageUrl,
                            base64Icon: ""
                        }
                    })
            });

            const isOurBranchSelected = () => ourBranch.arr.filter(elm => elm.selected).length > 0
            const isSharedSourceSelected = () => sharedSource.arr.filter(elm => elm.selected).length > 0

            let searchPlaceholder = computed(() => {
                if (isSharedSourceSelected()) {
                    return "Search by zip code"
                } else {
                    return "Search by city"
                }
            })

            const ATMS = reactive({ arr: [] })
            const SB = reactive({ arr: [] })

            const setSearch = () => {
                search.value = searchInput.value;

                if (isSharedSourceSelected()) {
                    getDataFomAPI()
                }
            };

            let geocoder;

            const initGeocoder = () => {
                loader
                    .load()
                    .then(() => {
                        return new google.maps.Geocoder();
                    })
                    .then((gc) => {
                        geocoder = gc;
                    })
                    .catch((e) => {
                        // do something
                        console.error(e);
                    });
            };


            const searchHandler = () => {
                clearTimeout(searchTimeout);
                searchTimeout = setTimeout(setSearch, 500);
            };

            // Initialize Filter
            const filters = reactive({ arr: [] });
            if (props.customFilters) {
                filters.arr = props.customFilters.map((filter) => {
                    return ({
                        id: filter.Id,
                        name: filter.Title,
                        backgroundColor: filter.BackgroundColor,
                        textColor: filter.TextColor,
                        selected: true
                    });
                })
            }

            const goToZipCode = () => {
                if (/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/.test(searchInput.value)) {
                    geocoder.geocode({ address: searchInput.value })
                        .then((res, status) => {
                            const lat = res.results[0].geometry.location.lat();
                            const lng = res.results[0].geometry.location.lng();
                            setTimeout(() => {
                                latitude.value = lat;
                                longitude.value = lng;
                            }, 1000)
                        })
                        .catch(err => console.error(err))
                }
            }



            /* TODO Use typescript
             *
             * EXTERNAL SOURCE OPTIONS:
             *
             * 1 -> None
             * 2 -> ATMs
             * 3 -> Shared Branches
             */
            if (props.config.SbEnable) {
                const sharedBranch = props.locationTypes.find(elm => elm.ExternalSource == "3")
                const atm = props.locationTypes.find(elm => elm.ExternalSource == "2")

                if (typeof sharedBranch != 'undefined') {
                    sharedSource.arr = [
                        ...sharedSource.arr,
                        {
                            id: "SHAREDBRANCH",
                            name: sharedBranch.Title,
                            backgroundColor: sharedBranch.BackgroundColor,
                            textColor: sharedBranch.TextColor,
                            selected: true,
                            icon: sharedBranch.ImageUrl,
                            base64Icon: ""
                        }
                    ]

                    if (sharedBranch.ImageUrl) {
                        urlContentToDataUri(sharedBranch.ImageUrl)
                            .then(dataUrl => {
                                sharedSource.arr = sharedSource.arr.map(elm => {
                                    if (elm.id == "SHAREDBRANCH") {
                                        return ({
                                            ...elm,
                                            base64Icon: dataUrl
                                        })
                                    }
                                    return elm
                                })
                            });
                    }
                }

                if (typeof atm != 'undefined') {
                    sharedSource.arr = [
                        ...sharedSource.arr,
                        {
                            id: "ATM",
                            name: atm.Title,
                            backgroundColor: atm.BackgroundColor,
                            textColor: atm.TextColor,
                            selected: true,
                            icon: atm.ImageUrl,
                            base64Icon: ""
                        }
                    ]

                    if (atm.ImageUrl) {
                        urlContentToDataUri(atm.ImageUrl)
                            .then(dataUrl => {
                                sharedSource.arr = sharedSource.arr.map(elm => {
                                    if (elm.id == "ATM") {
                                        return ({
                                            ...elm,
                                            base64Icon: dataUrl
                                        })
                                    }
                                    return elm
                                })
                            });
                    }

                }
            }

            const isSharedBranchSelected = () => {
                const sb = sharedSource.arr.find(elm => elm.id == "SHAREDBRANCH")

                if (typeof sb == "undefined") return false
                return sb.selected
            }

            const isATMSelected = () => {
                const atm = sharedSource.arr.find(elm => elm.id == "ATM")
                if (typeof atm == "undefined") return false
                return atm.selected
            }

            // Get custom filters from config
            if (props.customFilters) {
                filters.arr = props.customFilters.map((filter) => {
                    return ({
                        id: filter.Id,
                        name: filter.Title,
                        backgroundColor: filter.BackgroundColor,
                        textColor: filter.TextColor,
                        selected: true
                    });
                })
            }

            //Selection & click handlers
            const toggleSharedSource = (id) => {
                sharedSource.arr = sharedSource.arr.map(elm => elm.id == id ? { ...elm, selected: !elm.selected } : elm)
                getDataFomAPI()
            }

            const toggleOurBranch = (id) => {
                search.value = ""
                ourBranch.arr = ourBranch.arr.map(elm => elm.id == id ? { ...elm, selected: !elm.selected } : elm)

                if (!isOurBranchSelected()) {
                    filters.arr = filters.arr.map(elm => ({ ...elm, selected: false }))
                } else {
                    filters.arr = filters.arr.map(elm => ({ ...elm, selected: true }))
                }
            }

            const toggleFilter = (id) => {
                filters.arr = filters.arr.map(elm => elm.id == id ? { ...elm, selected: !elm.selected } : elm)
            }

            const selectedFilters = computed(() => filters.arr.filter(elm => elm.selected == true).map(item => item.id))

            const sharedSourceClickHandler = (opt) => toggleSharedSource(opt.id)
            const ourBranchClickHandler = (opt) => toggleOurBranch(opt.id)
            const filterClickHadler = (opt) => toggleFilter(opt.id)

            const getBranchesFromAPI = async (locatorType, zipCode = "") => {
                fetch('/get/sharedBranch', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        locatorType,
                        zipCode
                    })
                }).then(res => {
                    return res.json()
                }).then(res => {
                    const sharedBranch = props.locationTypes.find(elm => elm.ExternalSource == "3")
                    const atm = props.locationTypes.find(elm => elm.ExternalSource == "2")

                    const sbIcon = sharedSource.arr.find(elm => elm.id == "SHAREDBRANCH").base64Icon;
                    const atmIcon = sharedSource.arr.find(elm => elm.id == "ATM").base64Icon;

                    const pinIcon = locatorType == "S" && typeof sharedBranch != 'undefined' ? sharedBranch.ImageUrl : locatorType == "A" && typeof atm != 'undefined' ? atm.ImageUrl : null;
                    const base64Icon = locatorType == "S" && typeof sharedBranch != 'undefined' ? sbIcon : locatorType == "A" && typeof atm != 'undefined' ? atmIcon : null;
                    const pinBackground = locatorType == "S" && typeof sharedBranch != 'undefined' ? sharedBranch.BackgroundColor : locatorType == "A" && typeof atm != 'undefined' ? atm.BackgroundColor : "currentColor";
                    const pinBorder = locatorType == "S" && typeof sharedBranch != 'undefined' ? sharedBranch.TextColor : locatorType == "A" && typeof atm != 'undefined' ? atm.TextColor : "currentColor";

                    const maped = res.data.Locations.map(elm => {
                        return {
                            City: elm.City,
                            CustomFilters: elm.CustomFilters,
                            Details: elm.Details,
                            DisplayName: elm.InstitutionName,
                            Title: elm.InstitutionName,
                            Fax: elm.Fax,
                            Hours: elm.Hours,
                            Id: elm.Id,
                            ImageUrl: elm.ImageUrl,
                            Latitude: parseFloat(elm.Latitude),
                            Longitude: parseFloat(elm.Longitude),
                            Phone: elm.Phone,
                            State: elm.State,
                            Street: elm.Address,
                            Zip: elm.Zip,
                            Distance: distanceInMiBetweenEarthCoordinates(
                                parseFloat(elm.Latitude), parseFloat(elm.Longitude), props.center
                            ),
                            PinBackground: pinBackground,
                            PinIcon: pinIcon,
                            Base64PinIcon: base64Icon,
                            PinBorder: pinBorder
                        }
                    })

                    let innerSB = []
                    let innerATM = []

                    if (typeof sharedBranch != 'undefined') {
                        innerSB = props.locations.filter(elm => elm.LocationType.Id == sharedBranch.Id).map((loc) => ({
                            ...loc,
                            Distance: distanceInMiBetweenEarthCoordinates(
                                loc.Latitude,
                                loc.Longitude,
                                props.center
                            ),
                            PinIcon: pinIcon,
                            PinBackground: pinBackground,
                            Base64PinIcon: base64Icon,
                            PinBorder: pinBorder

                        }));
                    }

                    if (typeof atm != 'undefined') {
                        innerATM = props.locations.filter(elm => elm.LocationType.Id == atm.Id).map((loc) => ({
                            ...loc,
                            Distance: distanceInMiBetweenEarthCoordinates(
                                loc.Latitude,
                                loc.Longitude,
                                props.center
                            ),
                            PinIcon: pinIcon,
                            PinBackground: pinBackground,
                            Base64PinIcon: base64Icon,
                            PinBorder: pinBorder
                        }));
                    }

                    if (locatorType == "S") {
                        SB.arr = maped.concat(innerSB)
                    }

                    if (locatorType == "A") {
                        ATMS.arr = maped.concat(innerATM)
                    }
                })
                    .then(() => true)
                    .catch(err => {
                        console.error("SHARED BRANCHES RESPONSE ERROR: ", err)
                    });
            }

            const getDataFomAPI = () => {
                const searchValue = search.value ? search.value.toLowerCase() : "";
                let promises = []
                if (isSharedBranchSelected()) { promises.push(getBranchesFromAPI("S", searchValue)) } else { SB.arr = [] };
                if (isATMSelected()) { promises.push(getBranchesFromAPI("A", searchValue)) } else { ATMS.arr = [] };
                Promise.all(promises).then(() => {
                    goToZipCode()
                })
            }

            // Locations
            const latitude = ref(props.center.lat);
            const longitude = ref(props.center.lng);
            const currentZoom = ref(props.zoom);

            let markers = computed(() => {
                return visibleLocations.value.map((loc) => ({
                    locationInfo: {
                        city: loc.City,
                        fax: loc.Fax,
                        hours: loc.Hours,
                        imageAlt: loc.ImageAlt,
                        imageUrl: loc.ImageUrl,
                        phone: loc.Phone,
                        state: loc.State,
                        street: loc.Street,
                        title: loc.DisplayName,
                        zip: loc.Zip,
                        pinBackground: loc.PinBackground,
                        pinIcon: loc.PinIcon,
                        pinBorder: loc.PinBorder,
                        base64PinIcon: loc.Base64PinIcon
                    },
                    position: {
                        lat: loc.Latitude,
                        lng: loc.Longitude,
                    },
                }));
            });

            const setMapFromUsersLocation = (location) => {
                if (location.coords) {
                    latitude.value = location.coords.latitude;
                    longitude.value = location.coords.longitude;
                }
            };

            const getLocation = () => {
                if (window.navigator && window.navigator.geolocation) {
                    window.navigator.geolocation.getCurrentPosition(
                        setMapFromUsersLocation,
                        (err) => console.error(err)
                    );
                }
            };

            function urlContentToDataUri(url) {
                return fetch(url)
                    .then(response => response.blob())
                    .then(blob => new Promise(callback => {
                        let reader = new FileReader();
                        reader.onload = function () { callback(this.result) };
                        reader.readAsDataURL(blob);
                    }));
            }

            // Helpers
            const visibleLocations = computed(() => {
                let vLocations = []

                if (props.locations && isOurBranchSelected()) {
                    const selectedLocationTypesId = ourBranch.arr.filter(elm => elm.selected).map(item => item.id);

                    vLocations = props.locations
                        .filter(item => selectedLocationTypesId.includes(item.LocationType.Id))
                        .map((loc) => ({
                            ...loc,
                            Distance: distanceInMiBetweenEarthCoordinates(
                                loc.Latitude,
                                loc.Longitude,
                                props.center
                            ),
                            PinIcon: ourBranch.arr.find(elm => elm.id == loc.LocationType.Id).icon,
                            PinBackground: ourBranch.arr.find(elm => elm.id == loc.LocationType.Id).backgroundColor,
                            PinBorder: ourBranch.arr.find(elm => elm.id == loc.LocationType.Id).textColor,
                            Base64PinIcon: ourBranch.arr.find(elm => elm.id == loc.LocationType.Id).base64Icon,
                        }));

                    // Our branch Filters
                    if (selectedFilters.value.length > 0) {
                        vLocations = vLocations.filter(elm => elm.CustomFilters && selectedFilters.value.filter(filterId => elm.CustomFilters.map(item => item.Id).includes(filterId)).length > 0).sort(orderByObjectNumber('Distance'));
                    }

                    // Search filter
                    if (search.value && !sharedSource.arr.filter(elm => elm.selected).length > 0) {
                        const lowerCaseSearch = search.value.toLowerCase();
                        vLocations = vLocations.filter((loc) => {
                            return (
                                loc.Title.toLowerCase().includes(lowerCaseSearch) ||
                                loc.DisplayName.toLowerCase().includes(lowerCaseSearch) ||
                                loc.City.toLowerCase().includes(lowerCaseSearch) ||
                                loc.State.toLowerCase().includes(lowerCaseSearch)
                            );
                        });
                    }
                }

                if (isSharedSourceSelected()) {
                    vLocations = vLocations.concat(ATMS.arr, SB.arr)
                }

                return vLocations.sort(orderByObjectNumber('Distance'));
            });

            onMounted(() => {
                getDataFomAPI();
                initGeocoder();
            });


            return {
                currentZoom,
                formatPhoneNumber,
                getLocation,
                latitude,
                longitude,
                markers,
                searchHandler,
                searchInput,
                filterClickHadler,
                visibleLocations,
                searchPlaceholder,
                filters,
                sharedSourceClickHandler,
                ourBranch,
                sharedSource,
                ourBranchClickHandler
            };
        }
    }
</script>

<style lang="scss" scoped></style>